import React, { useContext, useEffect, useState } from "react";
import { ReactComponent as ProfileIcon } from "../../assets/images/insight-profile.svg";
import { ReactComponent as ArrowDown } from "../../assets/images/arrow-down-usage.svg";
import { ReactComponent as ArrowUp } from "../../assets/images/arrow-up-usage.svg";
import "./ProjectScore.scss";
import { useTranslation } from "react-i18next";
import AppContext from "../../config/AppContext";
import { getProjectList, getProjectMemberList } from "../../services/UsageInsightService";
import { Avatar, Tooltip, CircularProgress, Box } from "@mui/material";
import { SpaRounded } from "@mui/icons-material";
import CustomTypography from "../../components/CustomTypography";
import { ReactComponent as EmptyProjectList } from "../../assets/images/ralvie/noProjects.svg";
import { ReactComponent as EmptyProjectListDark } from "../../assets/images/ralvie/noProjectsDark.svg";
import { ReactComponent as Info } from "../../assets/images/ralvie/worklog-info.svg"
import { ReactComponent as InfoDark } from "../../assets/images/ralvie/info-dark.svg"
import { ReactComponent as DownArrow } from "../../assets/images/ralvie/ArrowDown.svg";
import { ReactComponent as UpArrow } from "../../assets/images/ralvie/ArrowUp.svg";

const ProjectScore = ({ selectedDate }) => {
    const { t } = useTranslation();
    const { mode } = useContext(AppContext);
    const [expandedProject, setExpandedProject] = useState(null);
    const [sortDirection, setSortDirection] = useState("desc");
    const [projectData, setProjectData] = useState([]);
    const [projectMemberData, setProjectMemberData] = useState(null);
    const [isProjectLoading, setIsProjetLoading] = useState(false);
    const [isMemberLoading, setIsMemberLoading] = useState(false);

    const toggleProject = (e, projectId) => {
        if (expandedProject === projectId) {
            setExpandedProject(null);
        } else {
            setExpandedProject(projectId);
        }
    };

    const handleSort = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setExpandedProject(null);
        if (sortDirection === null || sortDirection === 'desc') {
            setSortDirection('asc');
        } else {
            setSortDirection('desc');
        }
    };

    const fetchProjectData = () => {
        setIsProjetLoading(true);
        let startDate = selectedDate.startOf("day");
        getProjectList({
            startDate: startDate.isUTC()
                ? startDate.format()
                : startDate.utc().format()
        })
            .then(response => {
                if (response.status === 200 && response.data?.code === "RCI0000") {
                    setProjectData(response.data?.data);
                } else {
                    setProjectData([]);
                }
            })
            .catch(error => {
                console.error("Error fetching project data:", error);
            })
            .finally(() => {
                setIsProjetLoading(false);
            });
    }

    const fetchProjectMemberData = (projectId) => {
        setIsMemberLoading(true);
        setProjectMemberData(null)
        let startDate = selectedDate.startOf("day");
        getProjectMemberList({
            startDate: startDate.isUTC()
                ? startDate.format()
                : startDate.utc().format(),
            projectId: projectId,
            sortOrder: sortDirection
        })
            .then(response => {
                if (response.status === 200 && response.data?.code === "RCI0000") {
                    setProjectMemberData(response.data?.data);
                } else {
                    setProjectMemberData([]);
                }
            })
            .catch(error => {
                console.error("Error fetching project member data:", error);
            })
            .finally(() => {
                setIsMemberLoading(false);
            });
    }

    useEffect(() => {
        fetchProjectData();
        setExpandedProject(null);
    }, [selectedDate]);

    return (
        <div className={mode === "dark" ? "worklog-score-dark" : "worklog-score"}>
            <div className={mode === "dark" ? "worklog-score-title-dark" : "worklog-score-title"}>{t("RAL1223")}</div>

            <div className={mode === "dark" ? "worklog-table-header-dark" : "worklog-table-header"}>
                <span className={mode === "dark" ? "table-header-name-dark" : "table-header-name"}>{t("RAL0288")}</span>
                <span
                    className={`table-header-name sortable-header ${mode === 'dark' ? 'mode-edit-sort' : 'mode-view-sort'}`}
                    style={{ cursor: 'pointer' }}
                >
                    <span className="sort-icon" onClick={handleSort}>
                        <ArrowUp
                            className={`${mode === "dark" ? "arrow-icon-dark" : "arrow-icon-light"} ${sortDirection === 'asc' ? 'active' : 'inactive'}`}
                        />
                        <ArrowDown
                            className={`${mode === "dark" ? "arrow-icon-dark" : "arrow-icon-light"} ${sortDirection === 'desc' ? 'active' : 'inactive'}`}
                        />
                    </span>
                    <span>{t("RAL1215")}</span>
                    <Tooltip title={t("RAL1242")}>
                        {mode === "dark" ? <InfoDark /> : <Info />}
                    </Tooltip>
                </span>
            </div>
            <div className="worklog-table-scroll">
                {isProjectLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            height: '100%',
                            width: '100%',
                            paddingTop: 16,
                        }}
                    >
                        <CircularProgress id="project-circular-icon" />
                    </Box>
                ) : projectData?.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            paddingTop: 8
                        }}
                    >
                        {mode === "dark" ? <EmptyProjectListDark /> : <EmptyProjectList />}
                        <span
                            className={mode === "dark" ? "skip-text-dark" : "skip-text"}
                            style={{ marginTop: "10px" }}
                        >
                            {t('RAL0284')}
                        </span>
                    </Box>
                ) : (
                    projectData
                        .sort((a, b) => {
                            return sortDirection === 'asc'
                                ? a.projectUsageRate - b.projectUsageRate
                                : b.projectUsageRate - a.projectUsageRate;
                        })
                        .map((project, index) => {
                            return (
                                <div key={index}>
                                    <div
                                        className={mode === "dark" ? "group-title-dark" : "group-title"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            toggleProject(e, project?.id);
                                            if(expandedProject !== project.id )
                                            fetchProjectMemberData(project?.id);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <div
                                                style={{
                                                    width: 20,
                                                    height: 20,
                                                    backgroundColor: project?.color,
                                                    marginRight: "10px",
                                                    borderRadius: "5px",
                                                }}
                                            />
                                            <CustomTypography
                                                title={project?.name}
                                                mode={mode}
                                                sx={{
                                                    width: '260px',
                                                    '@media (max-width:1600px)': { width: '203px' },
                                                    '@media (max-width:1440px)': { width: '170px' },
                                                    '@media (max-width:1366px)': { width: '160px' },
                                                    '@media (max-width:1280px)': { width: '150px' }
                                                }}
                                            >
                                                {project?.name}
                                            </CustomTypography>
                                        </div>
                                        <div className="usage-project-arrow">
                                            <div>
                                                <span className={`log-score ${project?.projectUsageRate < 50 ? "red" : ""}`}>
                                                    {project?.projectUsageRate}%
                                                </span>
                                            </div>
                                            <div>
                                                {expandedProject === project.id ? (
                                                    <UpArrow />
                                                ) : (
                                                    <DownArrow />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {expandedProject === project.id ? (
                                        <div>
                                            <div className={mode === "dark" ? "worklog-table-header-dark" : "worklog-table-header"} style={{marginTop:'10px'}}>
                                                <span className={mode === "dark" ? "table-header-name-dark" : "table-header-name"}>{t("RAL0222")}</span>
                                                <span
                                                    className={`table-header-name sortable-header ${mode === 'dark' ? 'mode-edit-sort' : 'mode-view-sort'}`}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <span>{t("RAL1233")}</span>
                                                    <Tooltip title={t("RAL1234")}>
                                                        {mode === "dark" ? <InfoDark /> : <Info />}
                                                    </Tooltip>
                                                </span>
                                            </div>
                                            <div className="members-container">
                                                {isMemberLoading ? (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            height: '100%',
                                                            width: '100%',
                                                            paddingTop: 5,
                                                            paddingBottom: 5,
                                                        }}
                                                    >
                                                        <CircularProgress id="project-circular-icon" />
                                                    </Box>
                                                ) : (
                                                    projectMemberData && projectMemberData?.map((member, idx) => (
                                                        <div className="member-row" key={member._id}>
                                                            <div className="usage-member-info">
                                                                {member.profilePhoto ?
                                                                    <Avatar src={member.profilePhoto} alt="Member photo" sx={{ height: "32px", width: "32px" }} />
                                                                    : <ProfileIcon />}
                                                                <CustomTypography
                                                                    title={member.name}
                                                                    mode={mode}
                                                                    component={{
                                                                        typography: {
                                                                            className: mode === "dark" ? "usage-member-name-dark" : "usage-member-name",
                                                                            sx: {
                                                                                width: '280px',
                                                                                '@media (max-width:1600px)': { width: '233px' },
                                                                                '@media (max-width:1440px)': { width: '210px' },
                                                                                '@media (max-width:1366px)': { width: '200px' },
                                                                                '@media (max-width:1280px)': { width: '170px' }
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {member.name}
                                                                </CustomTypography>
                                                            </div>
                                                            <div className="usage-rate-container">
                                                                <span
                                                                    className={`log-score ${member.totalscore <= 3
                                                                        ? mode === "dark"
                                                                            ? "red-dark"
                                                                            : "red"
                                                                        : mode === "dark"
                                                                            ? "log-score-dark"
                                                                            : "log-score-light"
                                                                        }`}
                                                                >
                                                                    {member.totalscore.toString().padStart(2, "0")}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )))}
                                            </div>
                                        </div>
                                    ) : <></>}
                                </div>
                            );
                        }))}
            </div>
        </div>
    );
};

export default ProjectScore;